/**
 * Admin · Neighborhood Intelligence — per-area dashboard.
 *
 * Inventory, engagement, demand (searches), agents working the area, time
 * trends, and an inline Area-News manager (the CRUD half of the feature). Admin
 * chrome is English; neighborhood content is shown bilingually.
 */

import { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import DonutChart from '../../../components/charts/DonutChart';
import BarChart from '../../../components/charts/BarChart';
import LineChart from '../../../components/charts/LineChart';
import {
  fetchNeighborhood,
  downloadNeighborhoodCsv,
  deleteAreaNews,
  type NeighborhoodDashboard,
  type NeighborhoodPeriod,
  type AreaNewsItem,
} from '../../../services/neighborhoodService';
import NewsFormModal from './NewsFormModal';
import AreaBoundaryMap from './AreaBoundaryMap';

const PERIODS: { value: NeighborhoodPeriod; label: string }[] = [
  { value: '7d', label: '7d' },
  { value: '30d', label: '30d' },
  { value: '90d', label: '90d' },
  { value: '1y', label: '12m' },
];

const CATEGORY_LABELS: Record<string, string> = {
  villa: 'Villas', apartment: 'Apartments', building: 'Buildings', land: 'Land', commercial: 'Commercial', other: 'Other',
};
const CATEGORY_COLORS: Record<string, string> = {
  villa: '#004D40', apartment: '#055A4C', building: '#1B7566', land: '#22c55e', commercial: '#f59e0b', other: '#92A6A3',
};
const STATUS_BADGE: Record<string, string> = {
  PUBLISHED: 'bg-green-100 text-green-700', DRAFT: 'bg-gray-100 text-gray-500', ARCHIVED: 'bg-amber-100 text-amber-700',
};
const IMPACT_BADGE: Record<string, string> = {
  POSITIVE: 'bg-green-50 text-green-600', NEGATIVE: 'bg-red-50 text-red-600', NEUTRAL: 'bg-slate-50 text-slate-500', MIXED: 'bg-indigo-50 text-indigo-600',
};

function Kpi({ icon, label, value, sub }: { icon: string; label: string; value: string | number; sub?: string }) {
  return (
    <div className="rounded-2xl border border-gray-200 bg-white p-4">
      <div className="flex items-center gap-2 text-xs text-gray-400 mb-1.5"><i className={icon} /> {label}</div>
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      {sub && <div className="text-xs text-gray-400 mt-0.5">{sub}</div>}
    </div>
  );
}

function Section({ title, hint, children, action }: { title: string; hint?: string; children: React.ReactNode; action?: React.ReactNode }) {
  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-5 mb-5">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-base font-semibold text-[var(--pv-color-primary)]">{title}</h2>
          {hint && <p className="text-xs text-gray-400 mt-0.5">{hint}</p>}
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

export default function AdminNeighborhoodDetailPage() {
  const { areaId } = useParams<{ areaId: string }>();
  const [period, setPeriod] = useState<NeighborhoodPeriod>('30d');
  const [data, setData] = useState<NeighborhoodDashboard | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [newsModalOpen, setNewsModalOpen] = useState(false);
  const [editingNews, setEditingNews] = useState<AreaNewsItem | null>(null);

  const load = useCallback(async () => {
    if (!areaId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetchNeighborhood(areaId, period);
      setData(res.data);
    } catch {
      setError('Failed to load this neighborhood.');
    } finally {
      setLoading(false);
    }
  }, [areaId, period]);

  useEffect(() => { load(); }, [load]);

  const handleExport = () => {
    if (!areaId || !data) return;
    downloadNeighborhoodCsv(areaId, period, `neighborhood-${data.area.slug || areaId}.csv`).catch(() => {});
  };

  const handleDeleteNews = async (id: string) => {
    if (!window.confirm('Delete this news item?')) return;
    await deleteAreaNews(id).catch(() => {});
    load();
  };

  if (loading) {
    return <div className="min-h-screen bg-white flex items-center justify-center text-gray-400"><i className="ri-loader-4-line animate-spin text-3xl" /></div>;
  }
  if (error || !data) {
    return (
      <div className="min-h-screen bg-white flex flex-col items-center justify-center gap-3">
        <p className="text-red-500">{error || 'Not found'}</p>
        <Link to="/admin/neighborhoods" className="text-[#003930] hover:underline">Back to neighborhoods</Link>
      </div>
    );
  }

  const d = data;
  const scoreColor = d.activityScore >= 70 ? 'text-green-600' : d.activityScore >= 40 ? 'text-amber-600' : 'text-gray-400';

  // Chart datasets
  const mixData = d.inventory.byCategory.map((c) => ({ name: CATEGORY_LABELS[c.key] || c.key, value: c.count, color: CATEGORY_COLORS[c.key] || '#92A6A3' }));

  // Merge trends (views by day) + searches by month aren't same axis; show views/day line and searches/month + listings/month bars.
  const viewsLine = d.trends.viewsByDay.map((r) => ({ name: r.day.slice(5), views: r.count }));
  const listingsBars = d.trends.newListingsByMonth.map((r) => ({ name: r.month, value: r.count }));
  const demandSupplyBars = d.demandSupply.map((r) => ({ name: CATEGORY_LABELS[r.category] || r.category, value: r.viewsPerListing, color: CATEGORY_COLORS[r.category] || '#004D40' }));

  return (
    <div className="min-h-screen bg-white">
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
          <Link to="/admin/neighborhoods" className="text-sm text-gray-500 hover:text-[#003930] inline-flex items-center gap-1.5 mb-3">
            <i className="ri-arrow-left-line" /> All neighborhoods
          </Link>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div>
              <h1 className="text-2xl font-bold text-[var(--pv-color-primary)]">{d.area.nameEn}</h1>
              <p className="text-gray-500 mt-0.5">
                {d.area.nameAr && <span dir="rtl" className="me-2">{d.area.nameAr}</span>}
                {d.area.cityNameEn && <span>· {d.area.cityNameEn}</span>}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                {PERIODS.map((p) => (
                  <button key={p.value} onClick={() => setPeriod(p.value)} className={`px-3 py-1.5 text-sm ${period === p.value ? 'bg-[#004D40] text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}>{p.label}</button>
                ))}
              </div>
              <button onClick={handleExport} className="px-3 py-1.5 text-sm rounded-lg border border-gray-200 text-gray-700 hover:bg-gray-50 inline-flex items-center gap-1.5">
                <i className="ri-download-2-line" /> CSV
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Activity score + KPIs */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
          <div className="rounded-2xl border border-gray-200 bg-white p-4 col-span-2 md:col-span-1">
            <div className="text-xs text-gray-400 mb-1">Activity score</div>
            <div className={`text-3xl font-extrabold ${scoreColor}`}>{d.activityScore}</div>
            <div className="text-[11px] text-gray-400">/ 100</div>
          </div>
          <Kpi icon="ri-building-line" label="Active listings" value={d.inventory.totalActive.toLocaleString()} />
          <Kpi icon="ri-eye-line" label="Property views" value={d.engagement.propertyViewsAllTime.toLocaleString()} sub={`${d.engagement.propertyViewsPeriod.toLocaleString()} in period`} />
          <Kpi icon="ri-map-pin-line" label="Area views" value={d.engagement.areaCardViews.toLocaleString()} sub={`${d.engagement.areaUniqueViewers.toLocaleString()} unique`} />
          <Kpi icon="ri-search-line" label="Searches" value={d.demand.searches.toLocaleString()} />
          <Kpi icon="ri-heart-line" label="Favorites" value={d.engagement.favorites.toLocaleString()} />
          <Kpi icon="ri-mail-line" label="Inquiries" value={d.engagement.inquiries.toLocaleString()} />
        </div>

        {/* Charts row */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 mb-1">
          <Section title="Property mix" hint="Active listings by type">
            {mixData.length > 0 ? (
              <DonutChart data={mixData} height={260} centerLabel="Listings" centerValue={d.inventory.totalActive} />
            ) : <Empty />}
          </Section>
          <Section title="Views over time" hint="Property + neighborhood views per day">
            {viewsLine.length > 0 ? (
              <LineChart data={viewsLine} xAxisDataKey="name" lines={[{ dataKey: 'views', name: 'Views', color: '#004D40', strokeWidth: 2 }]} height={260} />
            ) : <Empty />}
          </Section>
          <Section title="Demand vs supply" hint="Views per active listing, by type - high bars = under-supplied demand">
            {demandSupplyBars.length > 0 ? (
              <BarChart data={demandSupplyBars} xAxisDataKey="name" colorByData height={260} />
            ) : <Empty />}
          </Section>
          <Section title="New listings" hint="Listings added per month (12 months)">
            {listingsBars.length > 0 ? (
              <BarChart data={listingsBars} xAxisDataKey="name" bars={[{ dataKey: 'value', name: 'New listings', color: '#055A4C', radius: 4 }]} height={260} />
            ) : <Empty />}
          </Section>
        </div>

        {/* Sales & selling rate */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <Section title="Sales & selling rate" hint="Based on listings marked SOLD / RENTED (accuracy depends on data hygiene)">
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div className="rounded-xl border border-gray-200 p-3 text-center">
                <div className="text-2xl font-bold text-green-600">{d.sales.sellingRate}%</div>
                <div className="text-xs text-gray-400">Selling rate</div>
              </div>
              <div className="rounded-xl border border-gray-200 p-3 text-center">
                <div className="text-2xl font-bold text-gray-900">{d.sales.soldAllTime.toLocaleString()}</div>
                <div className="text-xs text-gray-400">Sold ({d.sales.soldInPeriod} in period)</div>
              </div>
              <div className="rounded-xl border border-gray-200 p-3 text-center">
                <div className="text-2xl font-bold text-gray-900">{d.sales.rentedAllTime.toLocaleString()}</div>
                <div className="text-xs text-gray-400">Rented ({d.sales.rentedInPeriod} in period)</div>
              </div>
            </div>
            {d.sales.soldByCategory.length > 0 ? (
              <BarChart
                data={d.sales.soldByCategory.map((c) => ({ name: CATEGORY_LABELS[c.key] || c.key, value: c.count, color: CATEGORY_COLORS[c.key] || '#22c55e' }))}
                xAxisDataKey="name" colorByData height={200}
              />
            ) : <Empty text="No sales recorded in this period." />}
          </Section>

          <Section title="Neighborhood border" hint={d.area.hasBoundary ? `Imported boundary${d.area.boundarySource ? ` · source: ${d.area.boundarySource}` : ''}` : 'No boundary imported yet'}>
            {d.area.hasBoundary && d.area.boundary ? (
              <AreaBoundaryMap
                boundary={d.area.boundary}
                center={d.area.latitude && d.area.longitude ? { lat: d.area.latitude, lng: d.area.longitude } : null}
              />
            ) : (
              <div className="rounded-xl bg-gray-50 border border-dashed border-gray-200 p-6 text-center text-sm text-gray-400">
                No border on file. Generate borders for this city with<br />
                <code className="text-xs text-gray-500">node scripts/generate-area-voronoi.js --city {d.area.cityNameEn || '<city>'}</code>
              </div>
            )}
          </Section>
        </div>

        {/* Agents working here */}
        <Section title="Agents working here" hint="Derived from active listings in this neighborhood">
          {d.agents.length === 0 ? <Empty text="No agents with active listings here yet." /> : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {d.agents.map((a) => (
                <Link key={a.id} to={`/broker/${a.id}`} className="flex items-center gap-3 p-3 rounded-xl border border-gray-200 hover:border-[var(--pv-color-accent-soft)] hover:bg-[var(--pv-color-surface-strong)] transition-colors">
                  <div className="w-11 h-11 rounded-full bg-[#004D40] flex items-center justify-center text-white font-bold overflow-hidden flex-shrink-0">
                    {a.avatarUrl ? <img src={a.avatarUrl} alt={a.name} className="w-full h-full object-cover" /> : a.name.charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-gray-900 truncate">{a.name}</div>
                    <div className="text-xs text-gray-500 flex items-center gap-2">
                      <span>{a.listingsInArea} listing{a.listingsInArea === 1 ? '' : 's'}</span>
                      {a.avgRating > 0 && <span className="flex items-center gap-0.5"><i className="ri-star-fill text-amber-400" />{a.avgRating.toFixed(1)}</span>}
                      {a.badge && <span className="px-1.5 py-0.5 rounded bg-[var(--pv-color-surface-sunken)] text-[#003930] text-[10px]">{a.badge.replace('_', ' ')}</span>}
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </Section>

        {/* Area news manager */}
        <Section
          title="Local news & updates"
          hint="Construction, infrastructure, events - published items appear on the public site for this area"
          action={
            <button onClick={() => { setEditingNews(null); setNewsModalOpen(true); }} className="px-3 py-1.5 text-sm rounded-lg bg-[#004D40] text-white hover:opacity-90 inline-flex items-center gap-1.5">
              <i className="ri-add-line" /> Add news
            </button>
          }
        >
          {d.news.length === 0 ? <Empty text="No news yet for this neighborhood." /> : (
            <div className="space-y-2">
              {d.news.map((n) => (
                <div key={n.id} className="flex items-start gap-3 p-3 rounded-xl border border-gray-100 hover:bg-gray-50">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium text-gray-900">{n.titleEn}</span>
                      <span className={`px-2 py-0.5 rounded text-[11px] font-medium ${STATUS_BADGE[n.status] || 'bg-gray-100 text-gray-500'}`}>{n.status}</span>
                      <span className={`px-2 py-0.5 rounded text-[11px] ${IMPACT_BADGE[n.impact] || 'bg-gray-50 text-gray-500'}`}>{n.impact}</span>
                      <span className="px-2 py-0.5 rounded text-[11px] bg-slate-50 text-slate-500">{n.category}</span>
                    </div>
                    {n.titleAr && <div className="text-sm text-gray-500 mt-0.5" dir="rtl">{n.titleAr}</div>}
                    <div className="text-xs text-gray-400 mt-1">
                      {new Date(n.startDate).toLocaleDateString()}{n.endDate ? ` → ${new Date(n.endDate).toLocaleDateString()}` : ''}
                    </div>
                  </div>
                  <div className="flex items-center gap-1 flex-shrink-0">
                    <button onClick={() => { setEditingNews(n); setNewsModalOpen(true); }} className="p-2 rounded-lg text-gray-400 hover:text-[#003930] hover:bg-[var(--pv-color-surface-sunken)]" title="Edit"><i className="ri-edit-line" /></button>
                    <button onClick={() => handleDeleteNews(n.id)} className="p-2 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50" title="Delete"><i className="ri-delete-bin-line" /></button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Section>
      </main>

      {newsModalOpen && (
        <NewsFormModal
          cityId={d.area.cityId}
          areaId={d.area.id}
          neighborhoodName={d.area.nameEn}
          existing={editingNews}
          onClose={() => setNewsModalOpen(false)}
          onSaved={() => { setNewsModalOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function Empty({ text = 'No data for this period.' }: { text?: string }) {
  return <div className="py-10 text-center text-gray-300 text-sm">{text}</div>;
}
